import axios from 'axios';
import { mockSegment } from './mocks/segment.mock';
import { mockBusinessDecision } from './mocks/businessDecision.mock';
import { mockBusiness } from './mocks/business.mock';
import { mockNaicsSearch } from './mocks/naicsSearch.mock';
import { mockIpCountry } from './mocks/ipCountry.mock';
import { mockPlaidConnect } from './mocks/plaidConnect.mock';
import { mockPlaidLinkToken } from './mocks/plaidLinkToken.mock';
import { mockSendStatementsLater } from './mocks/sendStatementsLater.mock';
import { mockSaveFiles } from './mocks/saveFiles.mock';
import { configure, makeUseAxios } from 'axios-hooks';
import MockAdapter from 'axios-mock-adapter';
import { mockUserValidate } from './mocks/userValidate.mock';
import { useStore } from 'react-redux';
import { useCancelableAxios } from './useCancellableAxios';
import { handleUnauthorized } from './authBridge';
import { tentoAuditHeaders } from './auditContext';

export const axiosInstance = axios.create({});
// CR-12 audit correlation: attach originating-app + per-journey flow id so the
// backend audit trail can chain a whole apply journey together.
axiosInstance.interceptors.request.use((config) => {
  try {
    Object.entries(tentoAuditHeaders()).forEach(([key, value]) => {
      if (config.headers && typeof config.headers.set === 'function') {
        config.headers.set(key, value);
      } else {
        config.headers = { ...(config.headers || {}), [key]: value };
      }
    });
  } catch (e) {
    // Correlation headers are best-effort; never block a request.
  }
  return config;
});
axiosInstance.interceptors.response.use(
  (id) => id,
  async (error) => {
    const originalRequest = error.config;
    // Expired/invalid Auth0 token: re-authenticate instead of letting the app
    // proceed and surface a generic "Error Creating Your Application" (#00013).
    if (error.response?.status === 401) {
      // Drop the stale token so no queued request reuses it.
      delete axiosInstance.defaults.headers.common['Authorization'];
      handleUnauthorized();
    }
    try {
      error.message =
        error.message +
        `(${
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.request.responseText ||
          JSON.stringify(error.response.data)
        })`;
    } catch (e) {}
    return Promise.reject(error);
  }
);
const useAxiosMocked = makeUseAxios({
  axios: axiosInstance,
});

if (process.env.STORYBOOK_TEST) {
  console.log('MOCKING DATA RETURNED FROM BACKEND FOR STORYBOOK TESTING!');
  const mockAxios = new MockAdapter(axiosInstance);
  mockNaicsSearch(mockAxios);
  mockSegment(mockAxios);
  mockBusiness(mockAxios);
  mockUserValidate(mockAxios);
  mockBusinessDecision(mockAxios);
  mockIpCountry(mockAxios);
  mockPlaidConnect(mockAxios);
  mockPlaidLinkToken(mockAxios);
  mockSendStatementsLater(mockAxios);
  mockSaveFiles(mockAxios);
} else {
  configure({
    axios: axiosInstance,
  });
}
export const useAxios = (...hookProps) => {
  const { getState } = useStore();
  const token = getState()?.user?.token;
  if (token) {
    axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + token;
  }

  return process.env.STORYBOOK_TEST ? useAxiosMocked(...hookProps) : useCancelableAxios(...hookProps);
};
